import React from "react"
import {useTheme} from "@material-ui/core"
import {ModernTheme} from "../../../theme/modern/tokens"

type MiniEcgProps = {
    height?: number
    className?: string
    color?: string
}

/**
 * Synthetic ECG strip used in the "review in progress" banner — the Gaussian
 * beat synthesis is ported verbatim from the prototype's widgets.js.
 *
 * Purely decorative: this is NOT patient signal data, so it is aria-hidden.
 */
export default function MiniEcg(props: MiniEcgProps) {
    const theme = useTheme<ModernTheme>();
    const {height = 44, className, color = theme.modern.accent} = props;

    const {d, w} = React.useMemo(() => {
        const H = height;
        const mid = H / 2;
        const beat = 110;
        const n = 5;
        const W = n * beat + 20;
        const g = (t: number, mu: number, s: number, a: number) =>
          a * Math.exp(-Math.pow(t - mu, 2) / (2 * s * s));

        let path = "";
        for (let x = 0; x <= W; x += 1.5) {
            const t = (x % beat) / beat;
            const v =
              g(t, 0.32, 0.03, 0.1) +
              g(t, 0.47, 0.012, -0.06) +
              g(t, 0.5, 0.014, 0.95) +
              g(t, 0.53, 0.013, -0.14) +
              g(t, 0.78, 0.07, 0.24);
            path += (path ? "L" : "M") + x + " " + (mid - v * 16).toFixed(1);
        }

        return {d: path, w: W}
    }, [height]);

    return (
      <svg className={className} width={w} height={height} viewBox={`0 0 ${w} ${height}`} aria-hidden="true"
           focusable="false">
          <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" opacity="0.9"/>
      </svg>
    )
}
